import React, { useState, useEffect, useRef } from "react";
import {
    Upload,
    Download,
    Save,
    X as XIcon,
    Edit2,
    Trash2,
    Image as ImageIcon,
    Package,
    DollarSign,
    Tag,
    Boxes,
    FileSpreadsheet,
    AlertCircle,
    CheckCircle,
    Search,
} from "lucide-react";
import { db, storage } from "../../firebase";
import {
    collection,
    doc,
    setDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ProductImporter = () => {
    // Estados principales
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [notification, setNotification] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    // Estados para edición
    const [editingProduct, setEditingProduct] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({});

    // Estados para búsqueda y filtros
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDepartment, setSelectedDepartment] = useState("Todos");

    // Estados para imagen
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageFile, setImageFile] = useState(null);

    // Estados para importar JSON
    const [showJsonInput, setShowJsonInput] = useState(false);
    const [jsonText, setJsonText] = useState("");

    // Refs
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);

    // Departamentos únicos
    const departments = ["Todos", ...new Set(products.map(p => p.departamento).filter(Boolean))];

    // Cargar productos al montar
    useEffect(() => {
        fetchProducts();
    }, []);

    // Filtrar productos
    useEffect(() => {
        let filtered = products;

        // Filtrar por búsqueda
        if (searchTerm) {
            filtered = filtered.filter(
                (p) =>
                    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    p.codigo.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filtrar por departamento
        if (selectedDepartment !== "Todos") {
            filtered = filtered.filter((p) => p.departamento === selectedDepartment);
        }

        setFilteredProducts(filtered);
    }, [searchTerm, selectedDepartment, products]);

    // Notificación
    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    // Obtener productos de Firestore
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "productos"), orderBy("codigo"));
            const snapshot = await getDocs(q);

            const productsData = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            setProducts(productsData);
            setFilteredProducts(productsData);
        } catch (error) {
            console.error("Error cargando productos:", error);
            showNotification("error", "Error al cargar productos");
        } finally {
            setLoading(false);
        }
    };

    // Parsear precio (quitar $, comas, etc.)
    const parsePrice = (value) => {
        if (!value || value === "-") return 0;
        const cleaned = String(value).replace(/[$,]/g, "");
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Parsear número de inventario
    const parseInventory = (value) => {
        if (!value || value === "-") return null;
        const parsed = parseInt(value);
        return isNaN(parsed) ? null : parsed;
    };

    // Leer archivo XLSX
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
                showNotification("error", "El archivo está vacío");
                return;
            }

            // Procesar productos - TODOS LOS CAMPOS EXPLÍCITAMENTE
            const processedProducts = jsonData.map((row, index) => {
                // Mapear columnas (manejo flexible de nombres)
                const codigo = String(row["Código"] || row["Codigo"] || row["codigo"] || "").trim();
                const nombre = String(row["Producto"] || row["producto"] || row["nombre"] || "").trim();
                const pCosto = parsePrice(row["P. Costo"] || row["P Costo"] || row["Precio Costo"] || 0);
                const pVenta = parsePrice(row["P. Venta"] || row["P Venta"] || row["Precio Venta"] || 0);
                const pMayoreo = parsePrice(row["P. Mayoreo"] || row["P Mayoreo"] || row["Precio Mayoreo"] || 0);
                const departamento = String(row["Departamento"] || row["departamento"] || row["categoria"] || "Sin Categoría").trim();
                const existencia = parseInventory(row["Existencia"] || row["existencia"]);
                const invMinimo = parseInventory(row["Inv. Mínimo"] || row["Inv Mínimo"] || row["Inventario Mínimo"]);
                const invMaximo = parseInventory(row["Inv. Máximo"] || row["Inv Máximo"] || row["Inventario Máximo"]);
                const tipoVenta = String(row["Tipo de Venta"] || row["Tipo Venta"] || "UNIDAD").trim();
                const claveProducto = String(row["Clave Producto / Servicio"] || row["Clave Producto"] || "").trim();
                const unidadMedida = String(row["Unidad Medida CFDI"] || row["Unidad Medida"] || "Q3").trim();
                const iva = parseFloat(row["IVA"] || row["iva"] || 16);

                // Retornar TODOS los campos explícitamente
                return {
                    codigo: codigo,
                    nombre: nombre,
                    pCosto: pCosto,
                    pVenta: pVenta,
                    pMayoreo: pMayoreo,
                    departamento: departamento,
                    existencia: existencia,
                    invMinimo: invMinimo,
                    invMaximo: invMaximo,
                    tipoVenta: tipoVenta,
                    claveProducto: claveProducto,
                    unidadMedida: unidadMedida,
                    iva: iva,
                    imagen: null, // Se puede agregar después mediante edición
                };
            });

            // Filtrar productos válidos (deben tener al menos código y nombre)
            const validProducts = processedProducts.filter(
                (p) => p.codigo && p.nombre
            );

            if (validProducts.length === 0) {
                showNotification("error", "No se encontraron productos válidos (necesitan Código y Producto)");
                return;
            }

            setPreviewData(validProducts);
            setShowPreview(true);
        } catch (error) {
            console.error("Error procesando archivo:", error);
            showNotification("error", "Error al procesar el archivo");
        }

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Procesar JSON pegado
    const processJsonText = () => {
        if (!jsonText.trim()) {
            showNotification("error", "Por favor pega el JSON");
            return;
        }

        try {
            const jsonData = JSON.parse(jsonText);

            // Verificar que sea un array
            if (!Array.isArray(jsonData)) {
                showNotification("error", "El JSON debe ser un array de productos");
                return;
            }

            if (jsonData.length === 0) {
                showNotification("error", "El JSON está vacío");
                return;
            }

            // Mapear productos desde JSON - TODOS LOS CAMPOS EXPLÍCITAMENTE
            const processedProducts = jsonData.map((item) => {
                return {
                    codigo: String(item.codigo || "").trim(),
                    nombre: String(item.producto || item.nombre || "").trim(),
                    pCosto: typeof item.pCosto === 'number' ? item.pCosto : 0,
                    pVenta: typeof item.pVenta === 'number' ? item.pVenta : 0,
                    pMayoreo: typeof item.pMayoreo === 'number' ? item.pMayoreo : 0,
                    departamento: String(item.departamento || "Sin Categoría").trim(),
                    existencia: item.existencia !== undefined ? item.existencia : null,
                    invMinimo: item.invMinimo !== undefined ? item.invMinimo : null,
                    invMaximo: item.invMaximo !== undefined ? item.invMaximo : null,
                    tipoVenta: String(item.tipoVenta || "UNIDAD").trim(),
                    claveProducto: String(item.claveProdServ || item.claveProducto || "").trim(),
                    unidadMedida: String(item.unidadMedida || "Q3").trim(),
                    iva: typeof item.iva === 'number' ? item.iva : 16,
                    imagen: item.imagen || null,
                };
            });

            // Filtrar productos válidos
            const validProducts = processedProducts.filter(
                (p) => p.codigo && p.nombre
            );

            if (validProducts.length === 0) {
                showNotification(
                    "error",
                    "No se encontraron productos válidos (necesitan codigo y producto/nombre)"
                );
                return;
            }

            setPreviewData(validProducts);
            setShowPreview(true);
            setShowJsonInput(false);
            setJsonText("");
        } catch (error) {
            console.error("Error parseando JSON:", error);
            showNotification("error", `Error al parsear JSON: ${error.message}`);
        }
    };

    // Guardar productos a Firestore
    const saveToFirestore = async () => {
        if (!previewData || previewData.length === 0) return;

        setUploading(true);
        setUploadProgress(0);
        let successCount = 0;
        let updatedCount = 0;
        let errorCount = 0;

        try {
            for (let i = 0; i < previewData.length; i++) {
                const product = previewData[i];

                try {
                    // Usar código como ID del documento
                    const docRef = doc(db, "productos", product.codigo);

                    // Verificar si existe para diferenciar entre nuevo y actualización
                    const existingDoc = await getDocs(
                        query(collection(db, "productos"))
                    ).then(snapshot =>
                        snapshot.docs.find(d => d.id === product.codigo)
                    );

                    // Guardar TODOS los campos explícitamente
                    await setDoc(docRef, {
                        codigo: product.codigo,
                        nombre: product.nombre,
                        pCosto: product.pCosto,
                        pVenta: product.pVenta,
                        pMayoreo: product.pMayoreo,
                        departamento: product.departamento,
                        existencia: product.existencia,
                        invMinimo: product.invMinimo,
                        invMaximo: product.invMaximo,
                        tipoVenta: product.tipoVenta,
                        claveProducto: product.claveProducto,
                        unidadMedida: product.unidadMedida,
                        iva: product.iva,
                        imagen: product.imagen,
                        updatedAt: new Date(),
                    }, { merge: true });

                    if (existingDoc) {
                        updatedCount++;
                    } else {
                        successCount++;
                    }
                } catch (error) {
                    console.error(`Error guardando ${product.codigo}:`, error);
                    errorCount++;
                }

                setUploadProgress(((i + 1) / previewData.length) * 100);
            }

            showNotification(
                "success",
                `✅ ${successCount + updatedCount} productos procesados (${successCount} nuevos, ${updatedCount} actualizados)${errorCount > 0 ? ` | ⚠️ ${errorCount} errores` : ""}`
            );

            setShowPreview(false);
            setPreviewData(null);
            fetchProducts();
        } catch (error) {
            console.error("Error guardando productos:", error);
            showNotification("error", "Error al guardar productos");
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Abrir modal de edición
    const openEditModal = (product) => {
        setEditingProduct(product);
        setEditForm({ ...product });
        setImageFile(null);
        setShowEditModal(true);
    };

    // Actualizar campo del formulario
    const updateFormField = (field, value) => {
        setEditForm((prev) => ({ ...prev, [field]: value }));
    };

    // Subir imagen a Firebase Storage
    const uploadImage = async (file, productCode) => {
        try {
            const storageRef = ref(storage, `productos/${productCode}/${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            return url;
        } catch (error) {
            console.error("Error subiendo imagen:", error);
            throw error;
        }
    };

    // Guardar cambios de edición
    const saveEdit = async () => {
        if (!editingProduct) return;

        setUploading(true);
        try {
            let imageUrl = editForm.imagen;

            // Si hay nueva imagen, subirla
            if (imageFile) {
                setUploadingImage(true);
                imageUrl = await uploadImage(imageFile, editForm.codigo);
                setUploadingImage(false);
            }

            // Actualizar en Firestore
            await updateDoc(doc(db, "productos", editingProduct.id), {
                ...editForm,
                imagen: imageUrl,
                updatedAt: new Date(),
            });

            showNotification("success", "Producto actualizado correctamente");
            setShowEditModal(false);
            setEditingProduct(null);
            setImageFile(null);
            fetchProducts();
        } catch (error) {
            console.error("Error actualizando producto:", error);
            showNotification("error", "Error al actualizar producto");
        } finally {
            setUploading(false);
            setUploadingImage(false);
        }
    };

    // Eliminar producto
    const deleteProduct = async (productId) => {
        if (!window.confirm("¿Estás seguro de eliminar este producto?")) return;

        try {
            await deleteDoc(doc(db, "productos", productId));
            showNotification("success", "Producto eliminado");
            fetchProducts();
        } catch (error) {
            console.error("Error eliminando producto:", error);
            showNotification("error", "Error al eliminar producto");
        }
    };

    // Exportar a Excel
    const exportToExcel = () => {
        if (filteredProducts.length === 0) {
            showNotification("error", "No hay productos para exportar");
            return;
        }

        const exportData = filteredProducts.map((p) => ({
            Código: p.codigo,
            Producto: p.nombre,
            "P. Costo": `$${p.pCosto.toFixed(2)}`,
            "P. Venta": `$${p.pVenta.toFixed(2)}`,
            "P. Mayoreo": `$${p.pMayoreo.toFixed(2)}`,
            Departamento: p.departamento,
            Existencia: p.existencia ?? "-",
            "Inv. Mínimo": p.invMinimo ?? "-",
            "Inv. Máximo": p.invMaximo ?? "-",
            "Tipo de Venta": p.tipoVenta,
            "Clave Producto / Servicio": p.claveProducto,
            "Unidad Medida CFDI": p.unidadMedida,
            IVA: p.iva,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Productos");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(data, `productos_${new Date().toISOString().split("T")[0]}.xlsx`);

        showNotification("success", "Productos exportados correctamente");
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <Package className="w-8 h-8 text-[#FF0000]" />
                    Gestión de Productos
                </h1>
                <p className="text-gray-600 mt-2">
                    Importa, edita y gestiona tu catálogo de productos con imágenes
                </p>
            </div>

            {/* Notificación */}
            {notification && (
                <div
                    className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${notification.type === "success"
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : notification.type === "error"
                            ? "bg-red-50 border border-red-200 text-red-800"
                            : "bg-blue-50 border border-blue-200 text-blue-800"
                        }`}
                >
                    {notification.type === "success" ? (
                        <CheckCircle className="w-5 h-5" />
                    ) : (
                        <AlertCircle className="w-5 h-5" />
                    )}
                    <span>{notification.message}</span>
                </div>
            )}

            {/* Controles superiores */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                    {/* Búsqueda */}
                    <div className="flex-1 w-full lg:w-auto">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Buscar por código o nombre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Filtro por departamento */}
                    <select
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                    >
                        {departments.map((dept) => (
                            <option key={dept} value={dept}>
                                {dept}
                            </option>
                        ))}
                    </select>

                    {/* Botones de acción */}
                    <div className="flex gap-3">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-2 px-4 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors"
                        >
                            <Upload className="w-4 h-4" />
                            Importar XLSX
                        </button>
                        <button
                            onClick={() => setShowJsonInput(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <FileSpreadsheet className="w-4 h-4" />
                            Importar JSON
                        </button>
                        <button
                            onClick={exportToExcel}
                            disabled={filteredProducts.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                            <Download className="w-4 h-4" />
                            Exportar
                        </button>
                    </div>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                />
            </div>

            {/* Estadísticas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Productos</p>
                            <p className="text-2xl font-bold text-gray-900">{products.length}</p>
                        </div>
                        <Boxes className="w-8 h-8 text-blue-500" />
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Departamentos</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {departments.length - 1}
                            </p>
                        </div>
                        <Tag className="w-8 h-8 text-purple-500" />
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Con Imagen</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {products.filter((p) => p.imagen).length}
                            </p>
                        </div>
                        <ImageIcon className="w-8 h-8 text-green-500" />
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Mostrando</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {filteredProducts.length}
                            </p>
                        </div>
                        <FileSpreadsheet className="w-8 h-8 text-[#FF0000]" />
                    </div>
                </div>
            </div>

            {/* Tabla de productos */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                    <div className="w-16 h-16 border-4 border-[#FF0000] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando productos...</p>
                </div>
            ) : filteredProducts.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No hay productos</p>
                    <p className="text-gray-500 text-sm mt-2">
                        Importa un archivo XLSX para comenzar
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                        Imagen
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                        Código
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                        Producto
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                                        Departamento
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                                        P. Venta
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                                        Existencia
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredProducts.map((product) => (
                                    <tr
                                        key={product.id}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="px-4 py-3">
                                            {product.imagen ? (
                                                <img
                                                    src={product.imagen}
                                                    alt={product.nombre}
                                                    className="w-12 h-12 object-cover rounded-lg"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                                    <ImageIcon className="w-6 h-6 text-gray-400" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="font-mono text-sm text-gray-900">
                                                {product.codigo}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="font-medium text-gray-900">
                                                {product.nombre}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                                {product.departamento}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="font-semibold text-green-600">
                                                ${product.pVenta.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="text-gray-700">
                                                {product.existencia ?? "N/A"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(product)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Editar"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => deleteProduct(product.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal de Preview */}
            {showPreview && previewData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">
                                Vista Previa - {previewData.length} Productos
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Revisa los datos antes de importar
                            </p>
                        </div>

                        <div className="flex-1 overflow-auto p-6">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                                Código
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                                Producto
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                                Departamento
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">
                                                P. Venta
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">
                                                IVA
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {previewData.slice(0, 50).map((product, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 font-mono">{product.codigo}</td>
                                                <td className="px-3 py-2">{product.nombre}</td>
                                                <td className="px-3 py-2">{product.departamento}</td>
                                                <td className="px-3 py-2 text-right">
                                                    ${product.pVenta.toFixed(2)}
                                                </td>
                                                <td className="px-3 py-2 text-right">{product.iva}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {previewData.length > 50 && (
                                    <p className="text-center text-gray-500 text-sm mt-4">
                                        ... y {previewData.length - 50} productos más
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50">
                            {uploading ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-700">Guardando productos...</span>
                                        <span className="text-gray-900 font-medium">
                                            {Math.round(uploadProgress)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className="bg-[#FF0000] h-2.5 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setShowPreview(false);
                                            setPreviewData(null);
                                        }}
                                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={saveToFirestore}
                                        className="px-6 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors"
                                    >
                                        Importar {previewData.length} Productos
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Edición */}
            {showEditModal && editingProduct && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-3xl w-full my-8">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">
                                Editar Producto: {editingProduct.nombre}
                            </h3>
                        </div>

                        <div className="p-6 space-y-4 max-h-[calc(90vh-200px)] overflow-y-auto">
                            {/* Imagen */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Imagen del Producto
                                </label>
                                <div className="flex items-center gap-4">
                                    {(editForm.imagen || imageFile) && (
                                        <img
                                            src={
                                                imageFile
                                                    ? URL.createObjectURL(imageFile)
                                                    : editForm.imagen
                                            }
                                            alt="Preview"
                                            className="w-24 h-24 object-cover rounded-lg"
                                        />
                                    )}
                                    <button
                                        onClick={() => imageInputRef.current?.click()}
                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        <ImageIcon className="w-4 h-4" />
                                        {editForm.imagen ? "Cambiar Imagen" : "Subir Imagen"}
                                    </button>
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setImageFile(e.target.files?.[0])}
                                        className="hidden"
                                    />
                                </div>
                            </div>

                            {/* Grid de campos */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Código
                                    </label>
                                    <input
                                        type="text"
                                        value={editForm.codigo}
                                        onChange={(e) => updateFormField("codigo", e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                        disabled
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre del Producto
                                    </label>
                                    <input
                                        type="text"
                                        value={editForm.nombre}
                                        onChange={(e) => updateFormField("nombre", e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Precio de Costo
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editForm.pCosto}
                                        onChange={(e) =>
                                            updateFormField("pCosto", parseFloat(e.target.value) || 0)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Precio de Venta
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editForm.pVenta}
                                        onChange={(e) =>
                                            updateFormField("pVenta", parseFloat(e.target.value) || 0)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Precio Mayoreo
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editForm.pMayoreo}
                                        onChange={(e) =>
                                            updateFormField("pMayoreo", parseFloat(e.target.value) || 0)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Departamento
                                    </label>
                                    <input
                                        type="text"
                                        value={editForm.departamento}
                                        onChange={(e) =>
                                            updateFormField("departamento", e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Existencia
                                    </label>
                                    <input
                                        type="number"
                                        value={editForm.existencia ?? ""}
                                        onChange={(e) =>
                                            updateFormField(
                                                "existencia",
                                                e.target.value ? parseInt(e.target.value) : null
                                            )
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Inv. Mínimo
                                    </label>
                                    <input
                                        type="number"
                                        value={editForm.invMinimo ?? ""}
                                        onChange={(e) =>
                                            updateFormField(
                                                "invMinimo",
                                                e.target.value ? parseInt(e.target.value) : null
                                            )
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Inv. Máximo
                                    </label>
                                    <input
                                        type="number"
                                        value={editForm.invMaximo ?? ""}
                                        onChange={(e) =>
                                            updateFormField(
                                                "invMaximo",
                                                e.target.value ? parseInt(e.target.value) : null
                                            )
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tipo de Venta
                                    </label>
                                    <input
                                        type="text"
                                        value={editForm.tipoVenta}
                                        onChange={(e) => updateFormField("tipoVenta", e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Clave Producto
                                    </label>
                                    <input
                                        type="text"
                                        value={editForm.claveProducto}
                                        onChange={(e) =>
                                            updateFormField("claveProducto", e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Unidad Medida CFDI
                                    </label>
                                    <input
                                        type="text"
                                        value={editForm.unidadMedida}
                                        onChange={(e) =>
                                            updateFormField("unidadMedida", e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        IVA (%)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editForm.iva}
                                        onChange={(e) =>
                                            updateFormField("iva", parseFloat(e.target.value) || 0)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    setEditingProduct(null);
                                    setImageFile(null);
                                }}
                                disabled={uploading}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={saveEdit}
                                disabled={uploading || uploadingImage}
                                className="flex items-center gap-2 px-6 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors disabled:opacity-50"
                            >
                                {uploading || uploadingImage ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Guardar Cambios
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para Importar JSON */}
            {showJsonInput && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Importar Productos desde JSON
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-2">
                                        Pega tu array de productos en formato JSON
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowJsonInput(false);
                                        setJsonText("");
                                    }}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <XIcon className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto p-6">
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800 mb-2">
                                    <strong>Formato esperado:</strong> Un array JSON con objetos que contengan:
                                </p>
                                <ul className="text-xs text-blue-700 space-y-1 ml-4">
                                    <li>• <code>codigo</code> (requerido)</li>
                                    <li>• <code>producto</code> o <code>nombre</code> (requerido)</li>
                                    <li>• <code>pCosto</code>, <code>pVenta</code>, <code>pMayoreo</code></li>
                                    <li>• <code>departamento</code>, <code>existencia</code>, <code>invMinimo</code>, <code>invMaximo</code></li>
                                    <li>• <code>tipoVenta</code>, <code>claveProdServ</code>, <code>unidadMedida</code>, <code>iva</code></li>
                                </ul>
                            </div>

                            <textarea
                                value={jsonText}
                                onChange={(e) => setJsonText(e.target.value)}
                                placeholder='[
  {
    "codigo": "10",
    "producto": "Pollo Entero",
    "pCosto": 0.00,
    "pVenta": 260.00,
    "pMayoreo": 0.00,
    "departamento": "Pollos",
    "existencia": null,
    "invMinimo": null,
    "invMaximo": null,
    "tipoVenta": "UNIDAD",
    "claveProdServ": "90101801",
    "unidadMedida": "Q3",
    "iva": 16
  }
]'
                                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                style={{ resize: "none" }}
                            />

                            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                                <AlertCircle className="w-4 h-4" />
                                <span>
                                    Asegúrate de que el JSON sea válido. Los productos se validarán antes de importar.
                                </span>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowJsonInput(false);
                                    setJsonText("");
                                }}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={processJsonText}
                                disabled={!jsonText.trim()}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Procesar JSON
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductImporter;

